import { forwardRef, useEffect, useRef, useState, useImperativeHandle } from 'react';
import { Form, FormInstance, Input, Modal } from 'antd';
import RichTextEditor, { RichTextEditorHandle, RichTextEditorProps } from 'components/RichTextEditor';
import { FormBuilderFieldTextEditor } from 'types/formBuilder';
import { UploadFileApi } from 'types/upload-type';

interface FieldTextEditorProps {
  id?: string;
  value?: string;
  data: FormBuilderFieldTextEditor;
  form: FormInstance;
  disabled: boolean;
  onChange?: RichTextEditorProps['onChange'];
  onTextImageUpload?: (upload: UploadFileApi) => Promise<void | string>;
}

const FieldTextEditor = forwardRef<RichTextEditorHandle, FieldTextEditorProps>(
  ({ id, value, data, form, disabled, onChange, onTextImageUpload }, ref) => {
    const editorPageRef = useRef<RichTextEditorHandle>(null);
    const editorModalRef = useRef<RichTextEditorHandle>(null);
    const [openNotesModal, setOpenNotesModal] = useState(false);

    useEffect(() => {
      editorPageRef.current?.updateValue(value ?? '');
      editorModalRef.current?.updateValue(value ?? '');
    }, [value]);

    useImperativeHandle(ref, () => ({
      updateValue: (text: string) => editorPageRef.current?.updateValue(text),
      getQuill: () => editorPageRef.current?.getQuill() ?? null,
      getValue: () => editorPageRef.current?.getValue(),
      getYjs: () => editorPageRef.current?.getYjs() ?? '',
      focus: () => editorPageRef.current?.focus(),
      isEmpty: () => editorPageRef.current?.isEmpty() ?? true,
      setMentionContents: (mention: { id: number; name: string }) => editorPageRef.current?.setMentionContents(mention),
      hasPendingUploads: () => editorPageRef.current?.hasPendingUploads?.() ?? false,
      waitForUploads: () => editorPageRef.current?.waitForUploads?.() ?? Promise.resolve(),
    }));

    return (
      <>
        <Form.Item name={`${data.name}_delta`} noStyle>
          <Input type="hidden" />
        </Form.Item>

        <RichTextEditor
          ref={editorPageRef}
          id={id}
          className={data.scroll ? 'h-small' : 'min-h-small'}
          initialValue={value}
          readonly
          showMoreButton
          placeholder={data.placeholder}
        />

        <div
          style={{ position: 'absolute', height: '100%', width: '100%', top: 0, cursor: 'pointer' }}
          onClick={() => setOpenNotesModal(true)}
        />

        <Modal
          visible={openNotesModal}
          className="ant-modal-outside-close"
          width="100%"
          bodyStyle={{ padding: '24px 0' }}
          style={{ maxWidth: '1012px' }}
          onCancel={() => {
            if (editorModalRef.current && editorPageRef.current) {
              editorPageRef.current.updateValue(editorModalRef.current.getValue() ?? '');
            }
            setOpenNotesModal(false);
          }}
          footer={null}
          forceRender
        >
          <RichTextEditor
            ref={editorModalRef}
            id={id}
            initialValue={value}
            className="min-h-small"
            scrollingContainer={undefined}
            readonly={disabled}
            placeholder={data.placeholder}
            onChange={(value, delta, source, editor) => {
              const deltaJson = JSON.stringify(editor.getContents());

              form.setFieldsValue({
                [data.name]: value,
                [`${data.name}_delta`]: deltaJson,
              });
              onChange?.(value, delta, source, editor);
            }}
            onImageUploader={
              onTextImageUpload ||
              (async (upload) => {
                const currentUploads = form.getFieldValue('uploads');
                form.setFieldsValue({
                  uploads: currentUploads ? [...currentUploads, upload] : [upload],
                });
              })
            }
          />
        </Modal>
      </>
    );
  },
);

FieldTextEditor.displayName = 'FieldTextEditor';
export default FieldTextEditor;
